import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import {TagSchema} from 'constants/RegistryConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {schemaFieldsetName} from '../util/schemaFieldsetName';
import {GetImplementorsOfSchemaInterfaceQuery} from './__generated/GetImplementorsOfSchemaInterfaceQuery';
import RestrictToSchemaDropdown from './RestrictToSchemaDropdown';

export interface QueryingRestrictToSchemaDropdownProps {
  schemaInterfaceId: string;
  onSelectSchema: (selectedSchema: TagSchema | null) => void;
  selectedSchema?: TagSchema | null;
}

type GetImplementorsOfSchemaInterfaceOrInaccessible =
  GetImplementorsOfSchemaInterfaceQuery['schemaInterface'];

export type GetImplementorsOfSchemaInterface = Exclude<
  GetImplementorsOfSchemaInterfaceOrInaccessible,
  {__typename: 'InaccessibleInfoNode'}
>;

export const GET_IMPLEMENTORS_OF_SCHEMA_INTERFACE_QUERY: DocumentNode = gql`
  query GetImplementorsOfSchemaInterfaceQuery($id: StrictString!) @skipAudit @engComponent(name: SCHEMAS) {
    schemaInterface(id: $id) {
      ... on SchemaInterfaceNode {
        id
        schemas {
          ...TagSchemaGraphQL
        }
      }
    }
  }
  ${getFragmentWrapper('TagSchema').fragment}
`;

function QueryingRestrictToSchemaDropdown(props: QueryingRestrictToSchemaDropdownProps): JSX.Element | null {
  const schemaInterfaceResult = useBenchlingQuery<GetImplementorsOfSchemaInterfaceQuery>(
    GET_IMPLEMENTORS_OF_SCHEMA_INTERFACE_QUERY,
    {
      variables: {id: props.schemaInterfaceId},
      renderCachedDataWhenRefetching: true,
    }
  );
  if (schemaInterfaceResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (schemaInterfaceResult.type === QueryResultType.ERROR) {
    notifications.error(`Could not load ${schemaFieldsetName()}; try reloading the page.`);
    return null;
  }

  let schemaInterface: GetImplementorsOfSchemaInterface | null = null;
  if (schemaInterfaceResult.type === QueryResultType.SUCCESS) {
    const schemaInterfaceOrInaccessible = schemaInterfaceResult.data.schemaInterface;
    if (!isInaccessible(schemaInterfaceOrInaccessible)) {
      schemaInterface = schemaInterfaceOrInaccessible;
    }
  }
  const schemas = schemaInterface ? graphQLArrayToREST('TagSchema', schemaInterface.schemas) : [];
  return (
    <RestrictToSchemaDropdown
      schemas={schemas}
      onSelectSchema={props.onSelectSchema}
      selectedSchema={props.selectedSchema}
    />
  );
}

export default QueryingRestrictToSchemaDropdown;
